import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FileText, Calendar, CheckSquare, Plus, Clock, ChevronDown, ChevronUp, Edit2, House, Beer, X, MessageSquare, Mic, History, ArrowLeft, Eye, Bell } from 'lucide-react';
import { useCoachClientView } from '../../hooks/useCoachClientView';
import { EditClientModeModal } from './modals/EditClientModeModal';
import { ConfirmCoachedModeModal } from './modals/ConfirmCoachedModeModal';
import { ConfirmSelfManagedModal } from './modals/ConfirmSelfManagedModal';
import { CoachAssignmentModal } from './modals/CoachAssignmentModal';
import { EditClientPhaseModal } from './modals/EditClientPhaseModal';
import { ConfirmPhaseChangeModal } from './modals/ConfirmPhaseChangeModal';
import { ClientNotesPanel } from './ClientNotesPanel';
import { AllNotesWorkspace } from './AllNotesWorkspace';
import { MicroCalendar } from './MicroCalendar';
import { NutritionTile } from './NutritionTile';
import { NutritionExpandedView } from './NutritionExpandedView';
import { CalendarExpandedView } from './CalendarExpandedView';
import { UpcomingSessionsBlock } from './UpcomingSessionsBlock';
import { DateRange } from '../common/DateRangePicker';
import { ClientCalendarView } from './ClientCalendarView';
import { ActionItemsPanel } from './ActionItemsPanel';
import GoalManagement, { GoalManagementRef } from '../shared/GoalManagement';
import { NotificationPreferencesModal } from './NotificationPreferencesModal';
import { useDEAN } from '../../contexts/DEANContext';
import { useNotesWindow } from '../../contexts/NotesWindowContext';
import { useAuth } from '../../contexts/AuthContext';
import CareTeamTile from './CareTeamTile';

/**
 * CLIENT SUMMARY VIEW - "Mission Control" for Individual Client
 *
 * Three-column layout:
 * - Left: Client info, badges, action menus
 * - Center: Notes (Pinned, Last, Current)
 * - Right: Calendar widget, upcoming sessions, action items
 *
 * This is the new default view replacing ClientDashboardView.
 * Legacy view available at /staff/client/:clientId/legacy
 */
export const ClientSummaryView: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { clientData, loading, error, refresh } = useCoachClientView(clientId!);
  const { openPanel: openDEAN } = useDEAN();
  const { openWindow: openNotesWindow } = useNotesWindow();
  const { user: authUser } = useAuth();

  // Care team role for current user (security: controls UI access)
  const [myTeamRole, setMyTeamRole] = useState<'primary' | 'delegated' | 'secondary' | null>(null);
  const isAdmin = authUser?.role === 'admin';
  const hasWriteAccess = isAdmin || myTeamRole === 'primary' || myTeamRole === 'delegated';
  const isPrimaryCoach = isAdmin || myTeamRole === 'primary';

  // FAB menu state
  const [fabOpen, setFabOpen] = useState(false);

  // Goal management ref for triggering add modal
  const goalManagementRef = useRef<GoalManagementRef>(null);

  // Highlight stats for goals
  const [highlightStats, setHighlightStats] = useState<{ current: number; max: number; remaining: number } | null>(null);

  // Expanded view states
  const [nutritionExpanded, setNutritionExpanded] = useState(false);
  const [calendarExpanded, setCalendarExpanded] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState<number>(new Date().getMonth());
  const [calendarYear, setCalendarYear] = useState<number>(new Date().getFullYear());
  const [nutritionExternalMonth, setNutritionExternalMonth] = useState<{ month: number; year: number } | null>(null);

  const fmtDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  // Shared month sync — called from either calendar or nutrition controls
  const syncMonth = (month: number, year: number) => {
    setCalendarMonth(month);
    setCalendarYear(year);
    setNutritionExternalMonth({ month, year });
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    setNutritionDateRange({ startDate: fmtDate(first), endDate: fmtDate(last), preset: 'custom' });
  };

  const handleCalendarMonthChange = (month: number, year: number) => {
    syncMonth(month, year);
  };

  const handleNutritionMonthChange = (month: number, year: number) => {
    syncMonth(month, year);
  };
  const [nutritionDateRange, setNutritionDateRange] = useState<DateRange>(() => {
    const today = new Date();
    const start = new Date(today);
    start.setDate(start.getDate() - 29);
    return {
      startDate: start.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0],
      preset: 'last30'
    };
  });

  // Derive viewMode from URL path (not state) for persistence on refresh
  const getViewMode = (): 'summary' | 'notes' | 'goals' | 'calendar' => {
    if (location.pathname.endsWith('/notes')) return 'notes';
    if (location.pathname.endsWith('/goals')) return 'goals';
    if (location.pathname.endsWith('/calendar')) return 'calendar';
    return 'summary';
  };
  const viewMode = getViewMode();

  // Modal state - Mode (Self-Managed vs Coached)
  const [showEditModeModal, setShowEditModeModal] = useState(false);
  const [showConfirmCoachedModal, setShowConfirmCoachedModal] = useState(false);
  const [showConfirmSelfManagedModal, setShowConfirmSelfManagedModal] = useState(false);

  // Modal state - Coach Assignment
  const [showAssignCoachModal, setShowAssignCoachModal] = useState(false);
  const [assignedCoach, setAssignedCoach] = useState<{id: string; name: string} | null>(null);

  // Modal state - Phase
  const [showEditPhaseModal, setShowEditPhaseModal] = useState(false);
  const [showConfirmPhaseModal, setShowConfirmPhaseModal] = useState(false);
  const [selectedPhase, setSelectedPhase] = useState<number>(0);
  const [showNotifPrefsModal, setShowNotifPrefsModal] = useState(false);

  // Program start date editing
  const [editingStartDate, setEditingStartDate] = useState(false);
  const [startDateValue, setStartDateValue] = useState('');

  // Weight target editing
  const [editingGoalWeight, setEditingGoalWeight] = useState(false);
  const [goalWeightValue, setGoalWeightValue] = useState('');
  const [editingWeeklyLoss, setEditingWeeklyLoss] = useState(false);
  const [weeklyLossValue, setWeeklyLossValue] = useState('');

  // Last Note state
  const [lastNote, setLastNote] = useState<{
    note_id: string;
    title: string;
    session_date: string;
    created_by_name: string;
    signed_at: string | null;
    sections: {
      subjective?: { content: string };
      objective?: { coach_observations: string };
      assessment?: { content: string };
      plan?: { content: string };
    };
    tags: string[];
  } | null>(null);
  const [loadingLastNote, setLoadingLastNote] = useState(true);
  const [lastNoteExpanded, setLastNoteExpanded] = useState(false);

  // ESC key handler - contextual navigation:
  // - From nutrition expanded view → back to summary (same client)
  // - From notes/goals/calendar view → back to summary
  // - From summary view → back to client list
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Priority 1: Close expanded views
        if (calendarExpanded) {
          setCalendarExpanded(false);
          return;
        }
        if (nutritionExpanded) {
          setNutritionExpanded(false);
          return;
        }
        if (viewMode === 'notes' || viewMode === 'goals' || viewMode === 'calendar') {
          // Go back to summary (same client)
          navigate(`/staff/client/${clientId}`);
        } else {
          // Go back to client list
          navigate('/staff/clients');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate, viewMode, clientId, nutritionExpanded, calendarExpanded]);

  // Load assigned coach info
  useEffect(() => {
    if (clientData?.profile && !clientData.profile.self_managed) {
      loadAssignedCoach();
    } else {
      setAssignedCoach(null);
    }
  }, [clientData?.profile?.self_managed, clientId]);

  // Load last note
  useEffect(() => {
    loadLastNote();
  }, [clientId]);

  // Listen for notes-updated events from FloatingNotesWindow
  useEffect(() => {
    const handleNotesUpdated = (event: CustomEvent<{ clientId: string }>) => {
      // Only reload if the update was for this client
      if (event.detail.clientId === clientId) {
        loadLastNote();
      }
    };

    window.addEventListener('notes-updated', handleNotesUpdated as EventListener);
    return () => {
      window.removeEventListener('notes-updated', handleNotesUpdated as EventListener);
    };
  }, [clientId]);

  const loadLastNote = async () => {
    try {
      setLoadingLastNote(true);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/notes/${clientId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        // Get the most recent note (already sorted by date, pinned first)
        if (data.notes && data.notes.length > 0) {
          // Find the most recent by session_date, then by created_at for same-day notes
          const sortedByDate = [...data.notes].sort((a, b) => {
            const dateCompare = new Date(b.session_date).getTime() - new Date(a.session_date).getTime();
            if (dateCompare !== 0) return dateCompare;
            // Same session_date - sort by created_at (most recent first)
            return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
          });
          setLastNote(sortedByDate[0]);
        } else {
          setLastNote(null);
        }
      }
    } catch (error) {
      console.error('Error loading last note:', error);
      setLastNote(null);
    } finally {
      setLoadingLastNote(false);
    }
  };

  const loadAssignedCoach = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/coach/clients/${clientId}/assigned-coach`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.coach) {
          setAssignedCoach({
            id: data.coach.coach_id,
            name: data.coach.name || data.coach.email
          });
        } else {
          setAssignedCoach(null);
        }

        // Extract current user's role from care team
        if (data.care_team && authUser?.id) {
          const myEntry = data.care_team.find((m: any) => m.coach_id === authUser.id);
          setMyTeamRole(myEntry ? myEntry.role : null);
        } else {
          setMyTeamRole(null);
        }
      }
    } catch (error) {
      console.error('Error loading coach info:', error);
      setAssignedCoach(null);
      setMyTeamRole(null);
    }
  };

  // === Mode Change Handlers ===
  const handleModeChange = (newMode: 'self-managed' | 'coached') => {
    setShowEditModeModal(false);
    if (newMode === 'coached') {
      setShowConfirmCoachedModal(true);
    } else {
      setShowConfirmSelfManagedModal(true);
    }
  };

  const handleConfirmCoached = async (assignToMe: boolean) => {
    setShowConfirmCoachedModal(false);
    await updateClientMode(false);

    if (assignToMe) {
      await assignCoachToClient();
    }
  };

  const handleConfirmSelfManaged = async () => {
    setShowConfirmSelfManagedModal(false);
    await updateClientMode(true);
  };

  const updateClientMode = async (selfManaged: boolean) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/coach/clients/${clientId}/mode`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ self_managed: selfManaged })
      });

      if (response.ok) {
        await refresh();
      } else {
        const data = await response.json();
        alert(`Failed to update client mode: ${data.error}`);
      }
    } catch (error) {
      console.error('Error updating client mode:', error);
      alert('Failed to update client mode');
    }
  };

  // === Phase Change Handlers ===
  const handlePhaseSelection = (phase: number) => {
    setSelectedPhase(phase);
    setShowEditPhaseModal(false);
    setShowConfirmPhaseModal(true);
  };

  const handleConfirmPhaseChange = async () => {
    setShowConfirmPhaseModal(false);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/coach/clients/${clientId}/phase`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ client_phase: selectedPhase })
      });

      if (response.ok) {
        await refresh();
      } else {
        const data = await response.json();
        alert(`Failed to update phase: ${data.error}`);
      }
    } catch (error) {
      console.error('Error updating phase:', error);
      alert('Failed to update phase');
    }
  };

  // === Program Start Date Handler ===
  const handleEditStartDate = () => {
    setStartDateValue(clientData?.profile.program_start_date || '');
    setEditingStartDate(true);
  };

  const handleSaveStartDate = async () => {
    if (!startDateValue) return;
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/coach/clients/${clientId}/program-start`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ program_start_date: startDateValue })
      });

      if (response.ok) {
        setEditingStartDate(false);
        await refresh();
      } else {
        const data = await response.json();
        alert(`Failed to update program start date: ${data.error}`);
      }
    } catch (error) {
      console.error('Error updating program start date:', error);
      alert('Failed to update program start date');
    }
  };

  const handleClearStartDate = async () => {
    if (!confirm('Clear program start date? All historical data will be shown again.')) return;
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/coach/clients/${clientId}/program-start`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ program_start_date: null })
      });
      if (response.ok) {
        await refresh();
      }
    } catch (error) {
      console.error('Error clearing program start date:', error);
    }
  };

  // === Weight Target Handlers ===
  const handleEditGoalWeight = () => {
    setGoalWeightValue(clientData?.profile.goal_weight?.toString() || '');
    setEditingGoalWeight(true);
  };

  const handleSaveGoalWeight = async () => {
    const value = parseFloat(goalWeightValue);
    if (!value || value <= 0) return;
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/coach/clients/${clientId}/tdee-profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ goal_weight: value })
      });
      if (response.ok) {
        setEditingGoalWeight(false);
        await refresh();
      } else {
        const data = await response.json();
        alert(`Failed to update goal weight: ${data.error}`);
      }
    } catch (error) {
      console.error('Error updating goal weight:', error);
      alert('Failed to update goal weight');
    }
  };

  const handleEditWeeklyLoss = () => {
    // Storage: positive=loss, UI: negative=loss — negate for display
    const stored = clientData?.profile.target_weight_loss_per_week;
    setWeeklyLossValue(stored != null ? (-stored).toString() : '');
    setEditingWeeklyLoss(true);
  };

  const handleSaveWeeklyLoss = async () => {
    const uiValue = parseFloat(weeklyLossValue);
    if (isNaN(uiValue)) return;
    // UI: negative=loss, Storage: positive=loss — negate for storage
    const storageValue = -uiValue;
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/coach/clients/${clientId}/tdee-profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ target_weight_loss_per_week: storageValue })
      });
      if (response.ok) {
        setEditingWeeklyLoss(false);
        await refresh();
      } else {
        const data = await response.json();
        alert(`Failed to update weekly loss target: ${data.error}`);
      }
    } catch (error) {
      console.error('Error updating weekly loss target:', error);
      alert('Failed to update weekly loss target');
    }
  };

  // === Coach Assignment Handlers ===
  const handleManageCoach = () => {
    setShowEditModeModal(false);
    setShowAssignCoachModal(true);
  };

  const assignCoachToClient = async (coachId?: string) => {
    setShowAssignCoachModal(false);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/coach/clients/${clientId}/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ coach_id: coachId })
      });

      if (response.ok) {
        await loadAssignedCoach();
      } else {
        const data = await response.json();
        alert(`Failed to assign coach: ${data.error}`);
      }
    } catch (error) {
      console.error('Error assigning coach:', error);
      alert('Failed to assign coach');
    }
  };

  if (loading) {
    return (
      <div className="client-summary-view">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading client summary...</p>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  if (error || !clientData) {
    return (
      <div className="client-summary-view">
        <div className="error-container">
          <h2>Error Loading Client</h2>
          <p>{error || 'Failed to load client information'}</p>
          <button onClick={() => navigate(-1)} className="back-btn">
            <ArrowLeft size={16} />
            Back to Clients
          </button>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  const { profile } = clientData;

  return (
    <div className={`client-summary-view ${nutritionExpanded || calendarExpanded ? 'expanded-mode' : ''}`}>
      {/* CALENDAR EXPANDED MODE - Full viewport takeover */}
      {calendarExpanded && viewMode === 'summary' ? (
        <div className="nutrition-expanded-layout">
          {/* Mini Sidebar - Same as nutrition expanded */}
          <div className="mini-sidebar">
            <div className="mini-avatar">
              {profile.first_name?.[0]}{profile.last_name?.[0]}
            </div>
            <div className="mini-sidebar-divider" />
            <nav className="mini-nav">
              <button
                className="mini-nav-item"
                onClick={() => { setCalendarExpanded(false); navigate(`/staff/client/${clientId}`); }}
                title="Summary"
              >
                <House size={18} />
              </button>
              <button
                className="mini-nav-item"
                onClick={() => { setCalendarExpanded(false); navigate(`/staff/client/${clientId}/calendar`); }}
                title="Calendar"
              >
                <Calendar size={18} />
              </button>
              <button
                className="mini-nav-item"
                onClick={() => { setCalendarExpanded(false); navigate(`/staff/client/${clientId}/notes`); }}
                title="All Notes"
              >
                <FileText size={18} />
              </button>
              <button
                className="mini-nav-item"
                onClick={() => { setCalendarExpanded(false); navigate(`/staff/client/${clientId}/goals`); }}
                title="Goals"
              >
                <CheckSquare size={18} />
              </button>
              <button className="mini-nav-item" title="Notifications" onClick={() => setShowNotifPrefsModal(true)}>
                <Bell size={18} />
              </button>
            </nav>
          </div>

          {/* Expanded Calendar Content */}
          <div className="nutrition-expanded-main">
            <CalendarExpandedView
              clientId={clientId!}
              clientName={`${profile.first_name} ${profile.last_name || ''}`.trim()}
              clientTimezone={profile?.timezone || 'America/New_York'}
              initialMonth={calendarMonth}
              initialYear={calendarYear}
              onClose={(month, year) => { syncMonth(month, year); setCalendarExpanded(false); }}
            />
          </div>
        </div>
      ) : nutritionExpanded && viewMode === 'summary' ? (
        <div className="nutrition-expanded-layout">
          {/* Mini Sidebar - Collapsed left column */}
          <div className="mini-sidebar">
            <div className="mini-avatar">
              {profile.first_name?.[0]}{profile.last_name?.[0]}
            </div>
            <div className="mini-sidebar-divider" />
            <nav className="mini-nav">
              <button
                className="mini-nav-item"
                onClick={() => { setNutritionExpanded(false); navigate(`/staff/client/${clientId}`); }}
                title="Summary"
              >
                <House size={18} />
              </button>
              <button
                className="mini-nav-item"
                onClick={() => { setNutritionExpanded(false); navigate(`/staff/client/${clientId}/calendar`); }}
                title="Calendar"
              >
                <Calendar size={18} />
              </button>
              <button
                className="mini-nav-item"
                onClick={() => { setNutritionExpanded(false); navigate(`/staff/client/${clientId}/notes`); }}
                title="All Notes"
              >
                <FileText size={18} />
              </button>
              <button
                className="mini-nav-item"
                onClick={() => { setNutritionExpanded(false); navigate(`/staff/client/${clientId}/goals`); }}
                title="Goals"
              >
                <CheckSquare size={18} />
              </button>
              <button className="mini-nav-item" title="Notifications" onClick={() => setShowNotifPrefsModal(true)}>
                <Bell size={18} />
              </button>
            </nav>
          </div>

          {/* Expanded Nutrition Content */}
          <div className="nutrition-expanded-main">
            <NutritionExpandedView
              clientId={clientId!}
              clientName={`${profile.first_name} ${profile.last_name || ''}`.trim()}
              clientInitials={`${profile.first_name?.[0] || ''}${profile.last_name?.[0] || ''}`}
              dateRange={nutritionDateRange}
              onDateRangeChange={setNutritionDateRange}
              onMonthChange={handleNutritionMonthChange}
              onClose={() => setNutritionExpanded(false)}
            />
          </div>
        </div>
      ) : (
      /* Normal Three Column Layout */
      <div className="summary-grid">
        {/* LEFT COLUMN - Client Context */}
        <div className="column column-left">
          {/* Client Info Card */}
          <div className="card client-info-card">
            {/* Top row: Avatar + Name/Email/Start Date inline */}
            <div className="client-header-row">
              <div className="client-avatar">
                {profile.first_name?.[0]}{profile.last_name?.[0]}
              </div>
              <div className="client-header-text">
                <h2>{profile.first_name} {profile.last_name}</h2>
                <p className="client-email">{profile.email}</p>
                {editingStartDate ? (
                  <div className="inline-edit" style={{ marginTop: 4 }}>
                    <input
                      type="date"
                      value={startDateValue}
                      onChange={(e) => setStartDateValue(e.target.value)}
                      className="inline-edit-input"
                    />
                    <button className="badge badge-clickable badge-save" onClick={handleSaveStartDate}>Save</button>
                    <button className="badge badge-clickable" onClick={() => setEditingStartDate(false)}>
                      <X size={10} />
                    </button>
                  </div>
                ) : (
                  <div className="client-start-date">
                    <button
                      className="start-date-link"
                      onClick={handleEditStartDate}
                      title="Click to set program start date"
                    >
                      <Calendar size={11} />
                      {profile.program_start_date
                        ? `Started ${new Date(profile.program_start_date + 'T00:00:00').toLocaleDateString()}`
                        : 'Set Start Date'}
                      <Edit2 size={9} />
                    </button>
                    {profile.program_start_date && (
                      <button
                        className="start-date-clear"
                        onClick={handleClearStartDate}
                        title="Clear program start date"
                      >
                        <X size={10} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Status row: Phase + Mode */}
            <div className="badge-row">
              <button
                className="badge badge-phase badge-clickable"
                onClick={() => !profile.self_managed && setShowEditPhaseModal(true)}
                disabled={profile.self_managed}
                title={profile.self_managed ? 'Phase is fixed for self-managed clients' : 'Click to change phase'}
              >
                Phase {profile.self_managed ? '3' : (profile.client_phase ?? 0)}
                {!profile.self_managed && <Edit2 size={10} />}
              </button>
              <button
                className={`badge badge-clickable ${profile.self_managed ? 'badge-self' : 'badge-coached'}`}
                onClick={() => setShowEditModeModal(true)}
                title="Click to change coaching mode"
              >
                {profile.self_managed ? 'Self-Managed' : 'Coached'}
                <Edit2 size={10} />
              </button>
            </div>

            {/* Targets row: Goal Weight + Weekly Loss */}
            <div className="badge-row">
              {editingGoalWeight ? (
                <div className="inline-edit">
                  <input
                    type="number"
                    value={goalWeightValue}
                    onChange={(e) => setGoalWeightValue(e.target.value)}
                    className="inline-edit-input"
                    placeholder="lbs"
                    min="50"
                    step="1"
                  />
                  <span className="input-suffix">lbs</span>
                  <button className="badge badge-clickable badge-save" onClick={handleSaveGoalWeight}>Save</button>
                  <button className="badge badge-clickable" onClick={() => setEditingGoalWeight(false)}>
                    <X size={10} />
                  </button>
                </div>
              ) : (
                <button
                  className="badge badge-weight-target badge-clickable"
                  onClick={handleEditGoalWeight}
                  title="Click to set goal weight"
                >
                  {profile.goal_weight
                    ? `Goal: ${profile.goal_weight} lbs`
                    : 'Set Goal Weight'}
                  <Edit2 size={10} />
                </button>
              )}
              {editingWeeklyLoss ? (
                <div className="inline-edit">
                  <input
                    type="number"
                    value={weeklyLossValue}
                    onChange={(e) => setWeeklyLossValue(e.target.value)}
                    className="inline-edit-input"
                    placeholder="lbs/wk"
                    min="-3"
                    max="3"
                    step="0.5"
                  />
                  <span className="input-suffix">lbs/wk</span>
                  <button className="badge badge-clickable badge-save" onClick={handleSaveWeeklyLoss}>Save</button>
                  <button className="badge badge-clickable" onClick={() => setEditingWeeklyLoss(false)}>
                    <X size={10} />
                  </button>
                </div>
              ) : (
                <button
                  className="badge badge-weight-target badge-clickable"
                  onClick={handleEditWeeklyLoss}
                  title="Click to set weekly loss target"
                >
                  {profile.target_weight_loss_per_week != null
                    ? profile.target_weight_loss_per_week === 0
                      ? 'Maintain'
                      : profile.target_weight_loss_per_week > 0
                        ? `-${profile.target_weight_loss_per_week} lbs/wk`
                        : `+${Math.abs(profile.target_weight_loss_per_week)} lbs/wk`
                    : 'Set Weekly Target'}
                  <Edit2 size={10} />
                </button>
              )}
            </div>
          </div>

          {/* Action Menus */}
          <div className="card action-menus">
            <h3>Quick Actions</h3>
            <nav className="action-nav">
              <button
                className={`action-item ${viewMode === 'summary' ? 'active' : ''}`}
                onClick={() => navigate(`/staff/client/${clientId}`)}
              >
                <House size={16} />
                <span>Summary</span>
              </button>
              <button
                className={`action-item ${viewMode === 'calendar' ? 'active' : ''}`}
                onClick={() => navigate(`/staff/client/${clientId}/calendar`)}
              >
                <Calendar size={16} />
                <span>View Calendar</span>
              </button>
              <button
                className={`action-item ${viewMode === 'notes' ? 'active' : ''}`}
                onClick={() => navigate(`/staff/client/${clientId}/notes`)}
              >
                <FileText size={16} />
                <span>All Notes</span>
              </button>
              <button
                className={`action-item ${viewMode === 'goals' ? 'active' : ''}`}
                onClick={() => navigate(`/staff/client/${clientId}/goals`)}
              >
                <CheckSquare size={16} />
                <span>Goals</span>
              </button>
              <button className="action-item" onClick={() => setShowNotifPrefsModal(true)}>
                <Bell size={16} />
                <span>Notifications</span>
              </button>
            </nav>
          </div>

          {/* Care Team - only for coached clients */}
          {!profile.self_managed && (
            <CareTeamTile
              clientId={clientId!}
              onTeamChange={() => loadAssignedCoach()}
              canManageTeam={isPrimaryCoach}
            />
          )}
        </div>

        {/* WORKSPACE AREA - Switches between Summary and Notes views */}
        {viewMode === 'summary' ? (
          <>
            {/* CENTER COLUMN - Notes Focus */}
            <div className="column column-center">
              {/* Important Info + Pinned Notes Tabbed Panel */}
              <ClientNotesPanel clientId={clientId!} />

              {/* Nutrition Summary - Expands to full view */}
              <NutritionTile
                clientId={clientId!}
                dateRange={nutritionDateRange}
                onDateRangeChange={setNutritionDateRange}
                onExpand={() => setNutritionExpanded(true)}
                externalMonth={nutritionExternalMonth}
                onMonthChange={handleNutritionMonthChange}
              />

              {/* Last Note */}
              <div className={`card notes-section last-note-section ${lastNoteExpanded ? 'expanded' : ''}`}>
                <div className="section-header clickable" onClick={() => lastNote && setLastNoteExpanded(!lastNoteExpanded)}>
                  <h3><Clock size={16} /> Last Note</h3>
                  <div className="header-right">
                    {lastNote && (
                      <>
                        <span className="note-date-badge">{lastNote.session_date}</span>
                        <button className="expand-btn" title={lastNoteExpanded ? 'Collapse' : 'Expand'}>
                          {lastNoteExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {loadingLastNote ? (
                  <div className="placeholder-content">
                    <p>Loading...</p>
                  </div>
                ) : lastNote ? (
                  <div className="last-note-content">
                    <div className="last-note-header">
                      <span className="last-note-title">{lastNote.title}</span>
                      {lastNote.signed_at && (
                        <span className="signed-badge">Signed</span>
                      )}
                    </div>

                    {lastNoteExpanded ? (
                      /* Expanded view - show all SOAP sections */
                      <div className="last-note-full">
                        {lastNote.sections.subjective?.content && (
                          <div className="soap-section">
                            <h4>Subjective</h4>
                            <p>{lastNote.sections.subjective.content}</p>
                          </div>
                        )}
                        {lastNote.sections.objective?.coach_observations && (
                          <div className="soap-section">
                            <h4>Objective</h4>
                            <p>{lastNote.sections.objective.coach_observations}</p>
                          </div>
                        )}
                        {lastNote.sections.assessment?.content && (
                          <div className="soap-section">
                            <h4>Assessment</h4>
                            <p>{lastNote.sections.assessment.content}</p>
                          </div>
                        )}
                        {lastNote.sections.plan?.content && (
                          <div className="soap-section">
                            <h4>Plan</h4>
                            <p>{lastNote.sections.plan.content}</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      /* Collapsed view - 6-line preview */
                      <div className="last-note-preview">
                        {lastNote.sections.assessment?.content
                          || lastNote.sections.plan?.content
                          || lastNote.sections.subjective?.content
                          || 'No content preview available'
                        }
                      </div>
                    )}

                    <div className="last-note-meta">
                      <span>By {lastNote.created_by_name}</span>
                      {lastNote.tags.length > 0 && (
                        <span className="last-note-tags">
                          {lastNote.tags.map(tag => (
                            <span key={tag} className="note-tag">{tag}</span>
                          ))}
                        </span>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="placeholder-content">
                    <p>No previous notes</p>
                  </div>
                )}
              </div>

            </div>

            {/* RIGHT COLUMN - Schedule & Tasks */}
            <div className="column column-right">
              {/* Calendar Widget */}
              <MicroCalendar
                clientId={clientId!}
                clientTimezone={profile?.timezone || 'America/New_York'}
                initialMonth={calendarMonth}
                initialYear={calendarYear}
                onMonthChange={handleCalendarMonthChange}
                onExpand={(month, year) => { setCalendarMonth(month); setCalendarYear(year); setNutritionExpanded(false); setCalendarExpanded(true); }}
              />

              {/* Upcoming Sessions */}
              <UpcomingSessionsBlock
                clientId={clientId!}
                clientName={`${profile.first_name} ${profile.last_name || ''}`.trim()}
                clientTimezone={profile.timezone || 'America/New_York'}
                coachTimezone={Intl.DateTimeFormat().resolvedOptions().timeZone}
              />

              {/* Action Items / Todos */}
              <ActionItemsPanel
                clientId={clientId!}
                clientName={`${profile.first_name} ${profile.last_name || ''}`.trim()}
              />

              {/* Quick Actions FAB */}
              <div className={`fab-container ${fabOpen ? 'open' : ''}`}>
                {fabOpen && (
                  <div className="fab-menu">
                    <button
                      className="fab-menu-item dean"
                      onClick={() => {
                        const clientName = profile ? `${profile.first_name} ${profile.last_name || ''}`.trim() : 'Client';
                        openDEAN(clientId!, clientName);
                        setFabOpen(false);
                      }}
                    >
                      <Beer size={18} />
                      <span>Chat with DEAN</span>
                    </button>
                    <button
                      className="fab-menu-item"
                      onClick={() => {
                        const clientName = profile ? `${profile.first_name} ${profile.last_name || ''}`.trim() : 'Client';
                        openNotesWindow(clientId!, clientName);
                        setFabOpen(false);
                      }}
                    >
                      <MessageSquare size={18} />
                      <span>New Note</span>
                    </button>
                    <button className="fab-menu-item" onClick={() => setFabOpen(false)}>
                      <Mic size={18} />
                      <span>Transcribe Call</span>
                    </button>
                    <button
                      className="fab-menu-item legacy"
                      onClick={() => {
                        navigate(`/staff/client/${clientId}/legacy`);
                        setFabOpen(false);
                      }}
                    >
                      <History size={18} />
                      <span>Legacy View</span>
                    </button>
                  </div>
                )}
                <button className="fab" onClick={() => setFabOpen(!fabOpen)}>
                  {fabOpen ? <X size={24} /> : <Plus size={24} />}
                </button>
              </div>
              {fabOpen && <div className="fab-backdrop" onClick={() => setFabOpen(false)} />}
            </div>
          </>
        ) : viewMode === 'notes' ? (
          <div className="workspace-full">
            <AllNotesWorkspace
              clientId={clientId!}
              clientName={profile ? `${profile.first_name} ${profile.last_name || ''}`.trim() : undefined}
              onBack={() => navigate(`/staff/client/${clientId}`)}
            />
          </div>
        ) : viewMode === 'goals' ? (
          <div className="workspace-full goals-workspace">
            <div className="workspace-header">
              <h2>
                <CheckSquare size={20} /> Goals Management
                {highlightStats && (
                  <span className="highlight-counter" title="Goals highlighted on client's This Week tile">
                    <Eye size={14} /> {highlightStats.current}/{highlightStats.max}
                  </span>
                )}
              </h2>
              {!profile.self_managed && hasWriteAccess && (
                <button
                  className="btn-primary"
                  onClick={() => goalManagementRef.current?.openAddModal()}
                >
                  <Plus size={14} />
                  Add Goal
                </button>
              )}
            </div>
            <div className="goals-container">
              <GoalManagement
                ref={goalManagementRef}
                readOnly={profile.self_managed || !hasWriteAccess}
                userId={clientId}
                hideHeader={true}
                onHighlightStatsChange={setHighlightStats}
              />
            </div>
          </div>
        ) : viewMode === 'calendar' ? (
          <div className="workspace-full calendar-workspace">
            <div className="workspace-header">
              <button className="back-btn" onClick={() => navigate(`/staff/client/${clientId}`)}>
                <ArrowLeft size={16} />
                Back to Summary
              </button>
              <h2><Calendar size={20} /> Scheduling</h2>
            </div>
            <div className="calendar-container">
              <ClientCalendarView
                clientId={clientId!}
                clientName={`${profile.first_name} ${profile.last_name || ''}`.trim()}
                clientTimezone={profile.timezone || 'America/New_York'}
                onBack={() => navigate(`/staff/client/${clientId}`)}
              />
            </div>
          </div>
        ) : null}
      </div>
      )}

      {/* Modals */}
      <EditClientModeModal
        isOpen={showEditModeModal}
        onClose={() => setShowEditModeModal(false)}
        currentMode={profile.self_managed ? 'self-managed' : 'coached'}
        onSave={handleModeChange}
        onManageCoach={handleManageCoach}
        assignedCoachName={assignedCoach?.name}
      />

      <ConfirmCoachedModeModal
        isOpen={showConfirmCoachedModal}
        onClose={() => setShowConfirmCoachedModal(false)}
        onConfirm={handleConfirmCoached}
        clientName={`${profile.first_name} ${profile.last_name}`}
      />

      <ConfirmSelfManagedModal
        isOpen={showConfirmSelfManagedModal}
        onClose={() => setShowConfirmSelfManagedModal(false)}
        onConfirm={handleConfirmSelfManaged}
        clientName={`${profile.first_name} ${profile.last_name}`}
        assignedCoaches={assignedCoach ? [{ coach_id: assignedCoach.id, name: assignedCoach.name }] : []}
      />

      <CoachAssignmentModal
        isOpen={showAssignCoachModal}
        onClose={() => setShowAssignCoachModal(false)}
        onAssign={assignCoachToClient}
        currentCoachId={assignedCoach?.id}
        clientName={`${profile.first_name} ${profile.last_name}`}
        mode={assignedCoach ? 'change' : 'assign'}
      />

      <EditClientPhaseModal
        isOpen={showEditPhaseModal}
        onClose={() => setShowEditPhaseModal(false)}
        onSelect={handlePhaseSelection}
        currentPhase={profile.client_phase ?? 0}
      />

      <ConfirmPhaseChangeModal
        isOpen={showConfirmPhaseModal}
        onClose={() => setShowConfirmPhaseModal(false)}
        onConfirm={handleConfirmPhaseChange}
        currentPhase={profile.client_phase ?? 0}
        newPhase={selectedPhase}
        clientName={`${profile.first_name} ${profile.last_name}`}
      />

      <NotificationPreferencesModal
        isOpen={showNotifPrefsModal}
        onClose={() => setShowNotifPrefsModal(false)}
        clientId={clientId!}
        clientName={`${profile.first_name} ${profile.last_name || ''}`.trim()}
      />

      <style>{styles}</style>
    </div>
  );
};

const styles = `
  .client-summary-view {
    height: 100%;
    max-height: 100%;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    background: var(--bg-primary);
  }

  /* Lock scrolling when any view is expanded */
  .client-summary-view.expanded-mode {
    overflow: hidden;
  }

  /* Three Column Grid */
  .summary-grid {
    display: grid;
    grid-template-columns: 280px 1fr 320px;
    gap: 24px;
    padding: 24px;
    max-width: 1600px;
    margin: 0 auto;
    flex: 1;
    width: 100%;
    align-content: start;
  }

  .column {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  /* Explicitly anchor left column to grid column 1 */
  .column-left {
    grid-column: 1;
  }

  .column-right {
    align-self: start;
  }

  /* Nutrition Expanded Layout */
  .nutrition-expanded-layout {
    display: flex;
    flex: 1;
    gap: 16px;
    padding: 16px;
    background: var(--bg-primary);
    overflow: hidden;
    min-height: 0; /* Allow flex shrinking */
  }

  .mini-sidebar {
    width: 56px;
    background: var(--bg-secondary);
    border: 1px solid var(--border-secondary);
    border-radius: var(--radius-large);
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 16px 8px;
    gap: 0;
    flex-shrink: 0;
  }

  .mini-avatar {
    width: 36px;
    height: 36px;
    background: linear-gradient(135deg, var(--accent-green), #059669);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    margin-bottom: 12px;
  }

  .mini-sidebar-divider {
    width: 24px;
    height: 1px;
    background: var(--border-secondary);
    margin-bottom: 12px;
  }

  .mini-nav {
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
  }

  .mini-nav-item {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background: transparent;
    border: none;
    border-radius: var(--radius-medium);
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.2s;
  }

  .mini-nav-item:hover {
    background: var(--bg-tertiary);
    color: var(--accent-green);
  }

  .nutrition-expanded-main {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: var(--bg-secondary);
    border: 1px solid var(--border-secondary);
    border-radius: var(--radius-large);
    min-height: 0; /* Allow flex shrinking */
  }

  /* Cards */
  .card {
    background: var(--bg-secondary);
    border: 1px solid var(--border-secondary);
    border-radius: var(--radius-large);
    padding: 20px;
  }

  .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  .section-header h3 {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
  }

  .section-header h3 svg {
    color: var(--accent-green);
  }

  /* Left Column - Client Info */
  .client-info-card {
    text-align: left;
  }

  .client-header-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
  }

  .client-avatar {
    width: 48px;
    height: 48px;
    min-width: 48px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--accent-green), #059669);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    font-weight: 700;
  }

  .client-header-text {
    min-width: 0;
  }

  .client-info-card h2 {
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .client-email {
    font-size: 12px;
    color: var(--text-secondary);
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .client-start-date {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 3px;
  }

  .start-date-link {
    background: none;
    border: none;
    padding: 0;
    font-size: 11px;
    color: var(--text-secondary);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    opacity: 0.8;
    transition: opacity 0.15s ease;
  }

  .start-date-link:hover {
    opacity: 1;
    color: var(--accent-green);
  }

  .start-date-link svg:last-child {
    opacity: 0;
    transition: opacity 0.15s ease;
  }

  .start-date-link:hover svg:last-child {
    opacity: 0.6;
  }

  .start-date-clear {
    background: none;
    border: none;
    padding: 2px;
    cursor: pointer;
    color: var(--text-secondary);
    opacity: 0;
    transition: opacity 0.15s ease;
    display: flex;
    align-items: center;
  }

  .client-start-date:hover .start-date-clear {
    opacity: 0.5;
  }

  .start-date-clear:hover {
    opacity: 1 !important;
    color: #ef4444;
  }

  .badge-row {
    display: flex;
    gap: 6px;
    align-items: center;
    flex-wrap: wrap;
    margin-top: 8px;
  }

  .badge {
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    border: none;
  }

  .badge-clickable {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    transition: all 0.15s ease;
    border: 1px solid transparent;
  }

  .badge-clickable:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .badge-clickable:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }

  .badge-clickable svg {
    opacity: 0.6;
  }

  .badge-clickable:hover:not(:disabled) svg {
    opacity: 1;
  }

  .badge-phase {
    background: rgba(59, 130, 246, 0.1);
    color: #2563eb;
  }

  .badge-phase:hover:not(:disabled) {
    background: rgba(59, 130, 246, 0.2);
    border-color: rgba(59, 130, 246, 0.3);
  }

  .badge-coached {
    background: rgba(16, 185, 129, 0.1);
    color: #059669;
  }

  .badge-coached:hover {
    background: rgba(16, 185, 129, 0.2);
    border-color: rgba(16, 185, 129, 0.3);
  }

  .badge-self {
    background: rgba(249, 115, 22, 0.1);
    color: #ea580c;
  }

  .badge-self:hover {
    background: rgba(249, 115, 22, 0.2);
    border-color: rgba(249, 115, 22, 0.3);
  }

  .badge-weight-target {
    background: rgba(100, 116, 139, 0.1);
    color: var(--text-secondary);
  }

  .badge-weight-target:hover {
    background: rgba(100, 116, 139, 0.2);
    border-color: rgba(100, 116, 139, 0.3);
  }

  .input-suffix {
    font-size: 11px;
    color: var(--text-secondary);
  }

  .badge-save {
    background: rgba(34, 197, 94, 0.15);
    color: #22c55e;
  }

  .inline-edit {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .inline-edit-input {
    padding: 4px 8px;
    border: 1px solid var(--border);
    border-radius: 6px;
    font-size: 12px;
    background: var(--bg-primary);
    color: var(--text-primary);
    max-width: 120px;
  }

  /* Action Menus */
  .action-menus h3 {
    font-size: 13px;
    font-weight: 600;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin: 0 0 12px;
  }

  .action-nav {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .action-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    background: transparent;
    border: none;
    border-radius: var(--radius-medium);
    color: var(--text-primary);
    font-size: 14px;
    cursor: pointer;
    transition: all 0.15s;
    text-align: left;
  }

  .action-item:hover {
    background: var(--bg-primary);
  }

  .action-item.active {
    background: var(--accent-green-bg);
    color: var(--accent-green);
  }

  .action-item.active svg {
    color: var(--accent-green);
  }

  .action-item svg {
    color: var(--text-secondary);
  }

  /* Workspace Full - spans center + right columns */
  .workspace-full {
    grid-column: 2 / 4;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  /* Goals Workspace */
  .goals-workspace {
    padding: 0 24px 24px;
  }

  .goals-workspace .workspace-header {
    display: flex;
    align-items: center;
    gap: 24px;
    padding: 16px 0;
    border-bottom: 1px solid var(--border-secondary);
    margin-bottom: 24px;
  }

  .goals-workspace .workspace-header h2 {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 20px;
    font-weight: 600;
    color: var(--text-primary);
    flex: 1;
    margin: 0;
  }

  .goals-workspace .workspace-header h2 svg {
    color: var(--accent-green);
  }

  .goals-workspace .workspace-header h2 .highlight-counter {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    background-color: var(--bg-tertiary);
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    color: var(--text-secondary);
    margin-left: 12px;
  }

  .goals-workspace .workspace-header h2 .highlight-counter svg {
    color: var(--text-secondary);
  }

  .goals-container {
    flex: 1;
    overflow-y: auto;
  }

  /* Goals workspace header with Add button */
  .goals-workspace .workspace-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .goals-workspace .workspace-header .btn-primary {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    background: var(--accent-green);
    border: none;
    border-radius: var(--radius-medium);
    color: white;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s;
  }

  .goals-workspace .workspace-header .btn-primary:hover {
    background: var(--accent-green-hover);
  }

  /* Calendar Workspace */
  .calendar-workspace {
    padding: 0 24px 24px;
  }

  .calendar-workspace .workspace-header {
    display: flex;
    align-items: center;
    gap: 24px;
    padding: 16px 0;
    border-bottom: 1px solid var(--border-secondary);
    margin-bottom: 24px;
  }

  .calendar-workspace .workspace-header h2 {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 20px;
    font-weight: 600;
    color: var(--text-primary);
    flex: 1;
    margin: 0;
  }

  .calendar-workspace .workspace-header h2 svg {
    color: var(--accent-green);
  }

  /* Nutrition Workspace - Full expanded view */
  .nutrition-workspace {
    background: var(--bg-primary);
    border-radius: var(--radius-large);
    overflow: hidden;
  }

  .calendar-container {
    flex: 1;
    min-height: 500px;
  }

  /* Center Column - Notes */
  .notes-section {
    min-height: 120px;
  }

  .current-notes {
    flex: 1;
    min-height: 200px;
  }

  /* Last Note Section */
  .last-note-section .note-date-badge {
    background: var(--accent-blue-bg, rgba(59, 130, 246, 0.1));
    color: var(--accent-blue, #3b82f6);
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
  }

  .last-note-content {
    background: var(--bg-primary);
    border-radius: var(--radius-medium);
    padding: 16px;
    border: 1px solid var(--border-secondary);
  }

  .last-note-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  .last-note-title {
    font-size: 15px;
    font-weight: 600;
    color: var(--text-primary);
  }

  .signed-badge {
    background: rgba(16, 185, 129, 0.1);
    color: #059669;
    padding: 3px 8px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 500;
  }

  .last-note-preview {
    font-size: 14px;
    line-height: 1.6;
    color: var(--text-secondary);
    margin-bottom: 12px;
    /* 6-line clamp for collapsed preview */
    display: -webkit-box;
    -webkit-line-clamp: 6;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .last-note-section .section-header.clickable {
    cursor: pointer;
    transition: background-color 0.15s ease;
    margin: -16px -16px 16px -16px;
    padding: 16px;
    border-radius: var(--radius-medium) var(--radius-medium) 0 0;
  }

  .last-note-section .section-header.clickable:hover {
    background: var(--bg-secondary);
  }

  .last-note-section .section-header .header-right {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .last-note-section .expand-btn {
    background: transparent;
    border: none;
    padding: 4px;
    cursor: pointer;
    color: var(--text-tertiary);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: all 0.15s ease;
  }

  .last-note-section .expand-btn:hover {
    background: var(--bg-tertiary);
    color: var(--text-primary);
  }

  /* Expanded full note view */
  .last-note-full {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 12px;
  }

  .last-note-full .soap-section h4 {
    font-size: 12px;
    font-weight: 600;
    color: var(--text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin: 0 0 6px 0;
  }

  .last-note-full .soap-section p {
    font-size: 14px;
    line-height: 1.6;
    color: var(--text-secondary);
    margin: 0;
    white-space: pre-wrap;
  }

  .last-note-meta {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 12px;
    color: var(--text-tertiary);
    padding-top: 12px;
    border-top: 1px solid var(--border-secondary);
  }

  .last-note-tags {
    display: flex;
    gap: 6px;
  }

  .note-tag {
    background: var(--bg-secondary);
    color: var(--text-secondary);
    padding: 2px 8px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 500;
  }

  .expand-btn {
    font-size: 12px;
    color: var(--text-secondary);
    background: transparent;
    border: none;
    cursor: pointer;
  }

  .expand-btn:hover {
    color: var(--accent-green);
  }

  /* Placeholder Content */
  .placeholder-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 24px;
    text-align: center;
    color: var(--text-secondary);
    background: var(--bg-primary);
    border-radius: var(--radius-medium);
    border: 2px dashed var(--border-secondary);
  }

  .placeholder-content p {
    margin: 0;
    font-size: 14px;
  }

  .placeholder-content .hint {
    font-size: 12px;
    margin-top: 4px;
    opacity: 0.7;
  }

  .mini-calendar {
    min-height: 100px;
  }

  /* Buttons */
  .btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    background: var(--accent-green);
    color: white;
    border: none;
    border-radius: var(--radius-medium);
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s;
  }

  .btn-primary:hover {
    background: #059669;
  }

  .btn-small {
    padding: 6px 12px;
    font-size: 12px;
  }

  .btn-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    background: transparent;
    border: 1px solid var(--border);
    border-radius: var(--radius-medium);
    color: var(--text-secondary);
    cursor: pointer;
  }

  .btn-icon:hover {
    background: var(--bg-primary);
    color: var(--accent-green);
    border-color: var(--accent-green);
  }

  /* FAB Container & Menu */
  .fab-container {
    position: fixed;
    bottom: 24px;
    right: 24px;
    z-index: 1000;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 12px;
  }

  .fab-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    z-index: 999;
  }

  .fab-menu {
    display: flex;
    flex-direction: column;
    gap: 8px;
    animation: fabMenuIn 0.2s ease-out;
  }

  @keyframes fabMenuIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .fab-menu-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 20px;
    background: var(--bg-primary);
    border: 1px solid var(--border-primary);
    border-radius: 28px;
    color: var(--text-primary);
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transition: all 0.15s;
  }

  .fab-menu-item:hover {
    background: var(--bg-secondary);
    border-color: var(--accent-green);
    transform: translateX(-4px);
  }

  .fab-menu-item.dean {
    background: linear-gradient(135deg, var(--bg-primary) 0%, rgba(16, 185, 129, 0.1) 100%);
    border-color: var(--accent-green);
  }

  .fab-menu-item.dean:hover {
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.25) 100%);
  }

  .fab-menu-item.legacy {
    opacity: 0.7;
  }

  .fab-menu-item.legacy:hover {
    opacity: 1;
  }

  .fab-menu-item svg {
    color: var(--accent-green);
  }

  .fab {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: var(--accent-green);
    color: white;
    border: none;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    z-index: 1001;
  }

  .fab:hover {
    background: #059669;
    transform: scale(1.05);
  }

  .fab-container.open .fab {
    background: var(--text-secondary);
  }

  .fab-container.open .fab:hover {
    background: var(--text-primary);
  }

  /* Loading & Error States */
  .loading-container,
  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    text-align: center;
    padding: 40px;
  }

  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid var(--border-secondary);
    border-top-color: var(--accent-green);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin-bottom: 16px;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* Responsive */
  @media (max-width: 1200px) {
    .summary-grid {
      grid-template-columns: 1fr 1fr;
    }

    .column-left {
      grid-column: 1 / -1;
      flex-direction: row;
      flex-wrap: wrap;
    }

    .column-left .card {
      flex: 1;
      min-width: 250px;
    }
  }

  @media (max-width: 768px) {
    .summary-grid {
      grid-template-columns: 1fr;
      padding: 16px;
    }

    .column-left {
      flex-direction: column;
    }
  }
`;

export default ClientSummaryView;
